import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Baby, Check, Church, Clock, Compass, Flower2, Hammer, Heart, Landmark, Loader2, MapPin,
    Mountain, Palette, ScrollText, ShoppingBag, SlidersHorizontal, Sparkles, Star,
    TreePine, Waves, Wine, X,
} from "lucide-react";
import { API, api } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";
import { getFavorites, subscribeFavorites, toggleFavorite } from "@/utils/favorites";

export const CATEGORY_ICONS = {
    history: Landmark, art: Palette, food: Wine, hidden: Compass, views: Mountain,
    shopping: ShoppingBag, religious: Church, family: Baby, nature: TreePine,
    sea: Waves, craft: Hammer, exclusive: Sparkles, wellness: Flower2, archeology: ScrollText,
};

const fmtDur = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ""}` : `${min}m`);
const expPhoto = (e) => (e.has_photo ? `${API}/public/experiences/${e.id}/photo` : null);
const DUR_FILTERS = [["", "expDurAll"], ["short", "expDurShort"], ["half", "expDurHalf"], ["full", "expDurFull"]];
const durMatch = (f, min) => (f === "short" ? min <= 180 : f === "half" ? min > 180 && min <= 420 : f === "full" ? min > 420 : true);

const cache = {};

/** Catalogo visivo delle esperienze reali di una categoria (Google Places).
 *  In modalità "roma" mostra solo Roma e dintorni (Lazio); in "italia" tutta
 *  l'Italia con filtri per regione e durata. Nessun prezzo: non verificato. */
export const ExperienceCatalog = ({ category, mode, addedIds, onAdd, onRemove, onClose }) => {
    const { lang, t } = useLang();
    const [items, setItems] = useState(cache[category] || null);
    const [error, setError] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [region, setRegion] = useState("");
    const [dur, setDur] = useState("");
    const Icon = CATEGORY_ICONS[category] || Landmark;
    const [favs, setFavs] = useState(getFavorites());
    useEffect(() => subscribeFavorites(setFavs), []);
    const favIds = favs.map((f) => f.id);

    useEffect(() => {
        setItems(cache[category] || null);
        setError(false);
        setRegion("");
        setDur("");
        if (cache[category]) return;
        api.get(`/public/experiences?category=${category}`)
            .then((r) => { cache[category] = r.data.experiences || []; setItems(cache[category]); })
            .catch(() => setError(true));
    }, [category]);

    const scoped = useMemo(() => {
        let list = items || [];
        if (mode === "roma") list = list.filter((e) => e.region === "Lazio");
        return list;
    }, [items, mode]);
    const regions = useMemo(() => [...new Set(scoped.map((e) => e.region))].sort(), [scoped]);
    const filtered = useMemo(
        () => scoped.filter((e) => (!region || e.region === region) && durMatch(dur, e.duration_min || 0)),
        [scoped, region, dur]
    );

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.3 }}
            className="mt-5 overflow-hidden rounded-2xl border border-amber-500/25 bg-[#0b0b0c]/90"
            data-testid={`exp-catalog-${category}`}>
            <div className="flex flex-wrap items-center gap-3 border-b border-amber-500/15 px-6 py-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/40 bg-amber-400/10 text-amber-300">
                    <Icon size={19} />
                </span>
                <div className="min-w-0 flex-1">
                    <h3 className="font-display text-2xl text-amber-50" data-testid="exp-catalog-title">{t.builder.interests[category]}</h3>
                    <p className="mt-0.5 text-xs text-stone-400">{t.builder.catSub[category]}</p>
                </div>
                <button data-testid="exp-filters-toggle" onClick={() => setShowFilters((f) => !f)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${showFilters ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/40 text-amber-200 hover:bg-amber-400/10"}`}>
                    <SlidersHorizontal size={12} /> {t.builder.expFilters}
                </button>
                <button data-testid="exp-catalog-close" onClick={onClose} aria-label={t.builder.expClose}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                    <X size={15} />
                </button>
            </div>

            {mode === "roma" && (
                <p className="border-b border-amber-500/10 bg-amber-400/5 px-6 py-2 text-[11px] text-amber-200/80" data-testid="exp-rome-note">{t.builder.expRomeNote}</p>
            )}

            <AnimatePresence>
                {showFilters && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden border-b border-amber-500/10">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-6 py-4" data-testid="exp-filters">
                            {mode !== "roma" && regions.length > 1 && (
                                <label className="flex items-center gap-2 text-[11px] text-stone-400">
                                    <span className="uppercase tracking-widest text-amber-500/80">{t.builder.expRegion}</span>
                                    <select data-testid="exp-filter-region" value={region} onChange={(e) => setRegion(e.target.value)}
                                        className="rounded-lg border border-amber-500/25 bg-[#0b0b0c] px-2.5 py-1.5 text-[11px] text-amber-100 focus:outline-none">
                                        <option value="">{t.builder.expAllRegions}</option>
                                        {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="flex items-center gap-2 text-[11px] text-stone-400">
                                <span className="uppercase tracking-widest text-amber-500/80">{t.builder.expDur}</span>
                                <select data-testid="exp-filter-duration" value={dur} onChange={(e) => setDur(e.target.value)}
                                    className="rounded-lg border border-amber-500/25 bg-[#0b0b0c] px-2.5 py-1.5 text-[11px] text-amber-100 focus:outline-none">
                                    {DUR_FILTERS.map(([k, label]) => <option key={k} value={k}>{t.builder[label]}</option>)}
                                </select>
                            </label>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-h-[560px] overflow-y-auto px-6 py-5">
                {error && <p className="py-8 text-center text-xs text-stone-500" data-testid="exp-error">{t.builder.xp.errorMsg}</p>}
                {!error && !items && (
                    <p className="flex items-center justify-center gap-2 py-10 text-xs text-stone-500" data-testid="exp-loading">
                        <Loader2 size={14} className="animate-spin text-amber-400" /> {t.builder.expLoading}
                    </p>
                )}
                {items && filtered.length === 0 && !error && (
                    <p className="py-8 text-center text-xs text-stone-500" data-testid="exp-empty">{t.builder.expEmpty}</p>
                )}
                <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                    {(filtered || []).map((e) => {
                        const added = addedIds.includes(e.id);
                        return (
                            <div key={e.id} data-testid={`exp-card-${e.id}`}
                                className="gold-card group flex flex-col overflow-hidden rounded-xl">
                                <div className="relative h-40 overflow-hidden">
                                    <GoldImage src={expPhoto(e)} alt={pick(e.name, lang)}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c]/85 to-transparent" />
                                    <span className="absolute bottom-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-[#0b0b0c]/85 px-2.5 py-1 text-[10px] font-semibold text-amber-200">
                                        <MapPin size={10} className="text-amber-400" /> {e.region}{e.locality ? ` · ${e.locality}` : ""}
                                    </span>
                                    <button data-testid={`exp-fav-${e.id}`} aria-label={t.nav.favorites}
                                        onClick={() => toggleFavorite({ id: e.id, name: pick(e.name, lang), link: `/itinerario/italia?categoria=${category}` })}
                                        className={`absolute right-2.5 top-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${favIds.includes(e.id) ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/40 bg-[#0b0b0c]/70 text-amber-200 hover:bg-amber-400 hover:text-black"}`}>
                                        <Heart size={13} className={favIds.includes(e.id) ? "fill-black" : ""} />
                                    </button>
                                </div>
                                <div className="flex flex-1 flex-col p-4">
                                    <p className="text-[9px] uppercase tracking-[0.16em] text-amber-500/80">{pick(e.type_label, lang)}</p>
                                    <h4 className="mt-1 font-display text-lg leading-snug text-amber-50">{pick(e.name, lang)}</h4>
                                    {pick(e.desc, lang) && <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400 line-clamp-2">{pick(e.desc, lang)}</p>}
                                    <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-stone-500">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock size={11} className="text-amber-500/80" />~{fmtDur(e.duration_min)} · {t.builder.expDurIndicative}
                                        </span>
                                        {e.rating && (
                                            <span className="inline-flex items-center gap-1">
                                                <Star size={11} className="fill-amber-400 text-amber-400" />{e.rating}
                                            </span>
                                        )}
                                    </div>
                                    <button data-testid={`exp-add-${e.id}`}
                                        onClick={() => (added ? onRemove(e.id) : onAdd(e))}
                                        className={`mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${
                                            added ? "border-emerald-500/50 bg-emerald-400/10 text-emerald-300" : "border-amber-500/50 text-amber-200 hover:bg-amber-400 hover:text-black"
                                        }`}>
                                        {added ? <><Check size={12} /> {t.builder.expAdded}</> : <>+ {t.builder.expAdd}</>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {items && filtered.length > 0 && (
                    <p className="mt-4 text-center text-[10px] text-stone-600" data-testid="exp-price-note">{t.builder.expPriceNote}</p>
                )}
            </div>
        </motion.div>
    );
};
